/**
 * LUNE — Moteur NLU léger (vanilla JS, zéro dépendance, zéro API).
 * Normalisation, détection d'intention par mots-clés, extraction de critères
 * (pièce/style/couleur) et de dimensions depuis du texte libre.
 */
window.Lune = window.Lune || {};

Lune.NLU = (function () {
  'use strict';

  function normalize(text) {
    if (!text) return '';
    return text
      .toString()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[’']/g, ' ')
      .replace(/[^a-z0-9\s.,x×]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  // Pluriels simples + synonymes fréquents utilisés dans le magasin.
  var SYNONYMS = [
    [/\bpapiers peints?\b/g, 'papier peint'],
    [/\bdecors?\b/g, 'decor'],
    [/\bmurs?\b/g, 'mur'],
    [/\bpieces?\b/g, 'piece'],
    [/\bcouleurs?\b/g, 'couleur'],
    [/\bfinitions?\b/g, 'finition'],
    [/\bmatieres?\b/g, 'matiere'],
    [/\bechantillons?\b/g, 'echantillon'],
    [/\bdelais?\b/g, 'delai']
  ];

  function canonicalize(text) {
    var t = normalize(text);
    SYNONYMS.forEach(function (pair) {
      t = t.replace(pair[0], pair[1]);
    });
    return t;
  }

  /**
   * Cherche la meilleure entrée de la base de connaissances correspondant au texte.
   * knowledge.intents : [{ id, keywords: [...], answer, link }]
   * Retourne l'intent trouvé ou null.
   */
  function matchIntent(text, knowledge) {
    var t = canonicalize(text);
    if (!t || !knowledge || !knowledge.intents) return null;

    var best = null;
    var bestScore = 0;

    knowledge.intents.forEach(function (intent) {
      var score = 0;
      (intent.keywords || []).forEach(function (kw) {
        var k = canonicalize(kw);
        if (!k) return;
        if (t.indexOf(k) !== -1) {
          // Les expressions plus longues/précises comptent davantage.
          score += k.split(' ').length;
        }
      });
      if (score > bestScore) {
        bestScore = score;
        best = intent;
      }
    });

    return bestScore > 0 ? best : null;
  }

  // Vocabulaire de reconnaissance pour le texte libre (Q1-Q3 implicites).
  var ROOM_TERMS = {
    'Salon': ['salon', 'sejour'],
    'Chambre': ['chambre', 'chambre a coucher'],
    'Suite / Hôtel': ['suite', 'hotel'],
    'Bureau': ['bureau', 'espace de travail'],
    'Restaurant': ['restaurant'],
    'Entrée / Couloir': ['entree', 'couloir', 'hall'],
    'Chambre enfant': ['chambre enfant', 'chambre bebe', 'chambre d enfant']
  };

  var STYLE_TERMS = {
    'Hôtel contemporain': ['hotel contemporain', 'hotel luxe', 'hotel chic'],
    'Japandi': ['japandi', 'japonais', 'scandinave'],
    'Minéral': ['mineral', 'pierre', 'marbre'],
    'Nature': ['nature', 'naturel', 'foret', 'vegetal'],
    'Floral': ['floral', 'fleurs', 'fleuri'],
    'New York / Industriel': ['new york', 'industriel', 'loft'],
    'Artistique': ['artistique', 'art', 'peinture'],
    'Minimaliste': ['minimaliste', 'epure', 'sobre'],
    'Élégant / intemporel': ['elegant', 'intemporel', 'classique chic']
  };

  var COLOR_TERMS = {
    'Beige / crème': ['beige', 'creme', 'sable'],
    'Blanc': ['blanc'],
    'Gris': ['gris'],
    'Noir': ['noir'],
    'Marron / terre': ['marron', 'terre', 'brun', 'terracotta'],
    'Vert': ['vert', 'kaki', 'olive'],
    'Bleu': ['bleu', 'marine', 'azur'],
    'Coloré': ['colore', 'vif', 'multicolore']
  };

  function findFromVocabulary(t, vocab) {
    var found = [];
    Object.keys(vocab).forEach(function (label) {
      var terms = vocab[label];
      for (var i = 0; i < terms.length; i++) {
        if (t.indexOf(terms[i]) !== -1) {
          found.push(label);
          break;
        }
      }
    });
    return found;
  }

  /**
   * Extrait pièce/style/couleur mentionnés dans un texte libre.
   * Utilisé quand le client écrit directement au lieu de suivre le questionnaire.
   */
  function extractCriteria(text) {
    var t = canonicalize(text);
    return {
      room: findFromVocabulary(t, ROOM_TERMS),
      style: findFromVocabulary(t, STYLE_TERMS),
      colors: findFromVocabulary(t, COLOR_TERMS)
    };
  }

  /**
   * Détecte des dimensions de mur dans du texte libre.
   * Supporte : "4,2m x 2,5m", "4.2 x 2.5", "420cm x 250cm", "4m20 x 2m50".
   * Retourne { widthM, heightM, areaM2 } ou null.
   */
  function extractDimensions(text) {
    if (!text) return null;
    var t = text.toLowerCase().replace(/,/g, '.');

    // Format "4m20" / "2m50" (mètres + centimètres accolés)
    var reMeterCm = /(\d+)\s*m\s*(\d{1,2})\b/g;
    var meterCmMatches = [];
    var mc;
    while ((mc = reMeterCm.exec(t)) !== null) {
      meterCmMatches.push(parseFloat(mc[1]) + parseFloat(mc[2]) / 100);
    }
    if (meterCmMatches.length >= 2) {
      return buildDimensions(meterCmMatches[0], meterCmMatches[1]);
    }

    // Format générique "4.2m x 2.5m" / "420cm x 250cm" / "4.2 x 2.5"
    var reGeneric = /(\d+(?:\.\d+)?)\s*(m|cm)?\s*(?:x|\*|×)\s*(\d+(?:\.\d+)?)\s*(m|cm)?/;
    var g = t.match(reGeneric);
    if (g) {
      var w = parseFloat(g[1]);
      var wUnit = g[2];
      var h = parseFloat(g[3]);
      var hUnit = g[4] || wUnit;
      if (wUnit === 'cm') w = w / 100;
      if (hUnit === 'cm') h = h / 100;
      return buildDimensions(w, h);
    }

    return null;
  }

  function buildDimensions(w, h) {
    if (!w || !h || w <= 0 || h <= 0 || w > 30 || h > 10) return null;
    return {
      widthM: Math.round(w * 100) / 100,
      heightM: Math.round(h * 100) / 100,
      areaM2: Math.round(w * h * 100) / 100
    };
  }

  return {
    normalize: normalize,
    canonicalize: canonicalize,
    matchIntent: matchIntent,
    extractCriteria: extractCriteria,
    extractDimensions: extractDimensions
  };
})();
